import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Film, Loader2, Link2, Upload, X } from "lucide-react";
import { endpoints, API_BASE } from "@/lib/api";
import { NewsBody } from "@/components/NewsBody";

const CHUNK = 8 * 1024 * 1024;
const abs = (u) => (u && u.startsWith("/api/") ? `${API_BASE}${u}` : u);

// Body textarea with "Add photo" / "Add video" that drop media exactly at the cursor.
export const NewsBodyEditor = ({ value, onChange }) => {
  const ta = useRef(null);
  const imgIn = useRef(null);
  const vidIn = useRef(null);
  const caret = useRef(null);
  const [busy, setBusy] = useState("");
  const [videoBox, setVideoBox] = useState(false);
  const [link, setLink] = useState("");
  const [preview, setPreview] = useState(false);

  const remember = () => { const el = ta.current; if (el) caret.current = [el.selectionStart, el.selectionEnd]; };
  const insert = (token) => {
    const [a, b] = caret.current || [value.length, value.length];
    const before = value.slice(0, a), after = value.slice(b);
    const pre = before && !before.endsWith("\n") ? "\n" : "";
    const post = after.startsWith("\n") ? "" : "\n";
    const next = `${before}${pre}${token}${post}${after}`;
    onChange(next);
    const pos = (before + pre + token + post).length;
    caret.current = [pos, pos];
    requestAnimationFrame(() => { const el = ta.current; if (el) { el.focus(); el.setSelectionRange(pos, pos); } });
  };
  const ask = (what) => (window.prompt(`Caption for this ${what} (optional)`) || "").replace(/[\]\n|]/g, " ").trim();

  const onImage = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image"); return; }
    setBusy("Uploading photo…");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await endpoints.uploadImage(fd);
      const cap = ask("photo");
      insert(`[image:${abs(data.url)}${cap ? `|${cap}` : ""}]`);
      toast.success("Photo added to the story");
    } catch (err) { toast.error(err?.response?.data?.detail || "Photo upload failed"); }
    setBusy("");
  };

  const onVideo = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("video/")) { toast.error("Please choose a video file"); return; }
    setVideoBox(false);
    try {
      setBusy("Preparing video…");
      const { data } = await endpoints.newsVideoInit({ title: file.name.replace(/\.[^.]+$/, ""), size: file.size });
      for (let off = 0; off < file.size;) {
        setBusy(`Uploading video… ${Math.round((off / file.size) * 100)}%`);
        const { data: r } = await endpoints.newsVideoChunk(data.id, file.slice(off, off + CHUNK));
        off = r.offset;
      }
      const { data: fin } = await endpoints.newsVideoFinalize(data.id);
      const cap = ask("video");
      insert(`[video:${fin.url}${cap ? `|${cap}` : ""}]`);
      toast.success("Video added — it will be playable after a few minutes of processing");
    } catch (err) { toast.error(err?.response?.data?.detail || "Video upload failed"); }
    setBusy("");
  };

  const addLink = () => {
    const u = link.trim();
    if (!/^https?:\/\/\S+$/.test(u)) { toast.error("Paste a full link starting with https://"); return; }
    const cap = ask("video");
    insert(`[video:${u}${cap ? `|${cap}` : ""}]`);
    setLink(""); setVideoBox(false);
  };

  const btn = "h-9 px-3 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 border border-[#273042] bg-[#0A0C10] text-slate-200 hover:text-white hover:border-[#E11D2A]/60 transition-colors disabled:opacity-50";
  return (
    <div data-testid="news-body-editor">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Body</span>
        <div className="flex flex-wrap gap-2">
          <button type="button" className={btn} disabled={!!busy} onMouseDown={remember} onClick={() => imgIn.current?.click()} data-testid="news-add-photo-btn"><ImagePlus className="w-4 h-4" /> Add photo</button>
          <button type="button" className={btn} disabled={!!busy} onMouseDown={remember} onClick={() => setVideoBox((v) => !v)} data-testid="news-add-video-btn"><Film className="w-4 h-4" /> Add video</button>
          <button type="button" className={btn} onClick={() => setPreview((p) => !p)} data-testid="news-preview-toggle">{preview ? "Edit" : "Preview"}</button>
        </div>
      </div>
      <input ref={imgIn} type="file" accept="image/*" className="hidden" onChange={onImage} data-testid="news-photo-file-input" />
      <input ref={vidIn} type="file" accept="video/*" className="hidden" onChange={onVideo} data-testid="news-video-file-input" />
      {videoBox && (
        <div className="mt-2 rounded-xl border border-[#273042] bg-[#0A0C10] p-3 flex flex-col sm:flex-row gap-2" data-testid="news-video-box">
          <button type="button" className={btn} onClick={() => vidIn.current?.click()} data-testid="news-video-upload-btn"><Upload className="w-4 h-4" /> Upload video file</button>
          <div className="flex-1 flex gap-2">
            <div className="flex-1 relative"><Link2 className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-500" />
              <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="or paste a YouTube / Vimeo / .mp4 link" data-testid="news-video-link-input"
                className="w-full h-9 pl-8 pr-2 rounded-lg bg-[#121620] border border-[#273042] text-white text-xs outline-none focus:border-[#E11D2A]/60" /></div>
            <button type="button" className={btn} onClick={addLink} data-testid="news-video-link-add">Insert</button>
            <button type="button" className="text-slate-500 hover:text-white" onClick={() => setVideoBox(false)} aria-label="Close"><X className="w-4 h-4" /></button>
          </div>
        </div>
      )}
      {busy && <div className="mt-2 text-xs text-amber-300 flex items-center gap-2" data-testid="news-media-busy"><Loader2 className="w-3.5 h-3.5 animate-spin" /> {busy}</div>}
      {preview ? (
        <div className="mt-1.5 rounded-xl bg-[#0A0C10] border border-[#273042] p-4 max-h-[60vh] overflow-y-auto" data-testid="news-body-preview"><NewsBody text={value} /></div>
      ) : (
        <textarea ref={ta} value={value} onChange={(e) => onChange(e.target.value)} onSelect={remember} onKeyUp={remember} onClick={remember} rows={10} data-testid="news-body-input"
          placeholder="Write your story… put the cursor where a photo or video should appear, then tap Add photo / Add video."
          className="mt-1.5 w-full rounded-xl bg-[#0A0C10] border border-[#273042] p-4 text-white text-sm outline-none focus:border-[#E11D2A]/60 font-mono leading-relaxed" />
      )}
      <p className="text-[11px] text-slate-500 mt-1">Media appear in the story as lines like <code>[image:…]</code> or <code>[video:…]</code>. Text after the <code>|</code> is the caption.</p>
    </div>
  );
};
